import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Login.css';

export default function Login() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [pageOffset, setPageOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const moveAmount = 15;
      const newOffset = { ...pageOffset };
      
      if (e.key === 'ArrowUp') {
        newOffset.y = pageOffset.y - moveAmount;
        e.preventDefault();
      } else if (e.key === 'ArrowDown') {
        newOffset.y = pageOffset.y + moveAmount;
        e.preventDefault();
      } else if (e.key === 'ArrowLeft') {
        newOffset.x = pageOffset.x - moveAmount;
        e.preventDefault();
      } else if (e.key === 'ArrowRight') {
        newOffset.x = pageOffset.x + moveAmount;
        e.preventDefault();
      }
      
      if (newOffset !== pageOffset) {
        setPageOffset(newOffset);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pageOffset]);

  const badges = [
    { icon: 'fas fa-graduation-cap', label: 'Smart Learning' },
    { icon: 'fas fa-chalkboard-teacher', label: 'Teacher Portal' },
    { icon: 'fas fa-user-graduate', label: 'Student Hub' },
    { icon: 'fas fa-chart-line', label: 'Analytics' },
    { icon: 'fas fa-shield-alt', label: 'Secure Access' },
    { icon: 'fas fa-cloud', label: 'Cloud Based' },
    { icon: 'fas fa-comments', label: 'Communication Hub' },
    { icon: 'fas fa-bus', label: 'Transport Tracking' },
  ];

  const containerStyle = {
    ...styles.container,
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    borderRadius: isMobile ? '16px' : '24px',
    margin: isMobile ? '0 auto' : '20px auto',
  };

  const leftPanelStyle = {
    ...styles.leftPanel,
    padding: isMobile ? '40px 24px 32px' : '60px 50px',
    order: isMobile ? 2 : 1,
  };

  const rightPanelStyle = {
    ...styles.rightPanel,
    padding: isMobile ? '40px 24px' : '60px 50px',
    order: isMobile ? 1 : 2,
    minHeight: isMobile ? 'auto' : '500px',
  };

  const heroTitleStyle = {
    ...styles.heroTitle,
    fontSize: isMobile ? '32px' : '48px',
    letterSpacing: isMobile ? '-1px' : '-2px',
  };

  const logoStyle = {
    ...styles.logo,
    width: isMobile ? '220px' : '350px',
  };

  const logoSectionStyle = {
    ...styles.logoSection,
    padding: isMobile ? '20px 24px' : '30px 40px',
    marginBottom: isMobile ? '24px' : '40px',
  };

  const loginTitleStyle = {
    ...styles.loginTitle,
    fontSize: isMobile ? '30px' : '40px',
  };

  const featureBadgesStyle = {
    ...styles.featureBadges,
    gap: isMobile ? '8px' : '12px',
  };

  const badgeStyleMobile = {
    ...styles.badge,
    padding: isMobile ? '8px 14px' : '12px 20px',
    fontSize: isMobile ? '12px' : '13px',
  };

  return (
    <div style={{...styles.page, transform: `translate(${pageOffset.x}px, ${pageOffset.y}px)`, transition: 'transform 0.15s ease-out'}}>
      <div style={styles.bgOrbs}>
        <div style={styles.orb1}></div>
        <div style={styles.orb2}></div>
        <div style={styles.orb3}></div>
      </div>

      <div style={containerStyle}>
        {/* Right Panel — shown first on mobile */}
        <div style={rightPanelStyle}>
          <div style={styles.loginBox}>
            <div style={styles.loginHeader}>
              <h2 style={loginTitleStyle}>Welcome</h2>
              <p style={styles.loginSubtitle}>Sign in to access your dashboard</p>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Username</label>
              <input type="email" style={styles.inputField} placeholder="you@school.edu" />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.inputLabel}>Password</label>
              <input type="password" style={styles.inputField} placeholder="Enter your password" />
            </div>

            <button style={styles.signinBtn}>Sign In to Dashboard</button>

            <p style={styles.footerText}>
              Don't have an account? <a href="#" style={styles.footerLink}>Request Access</a>
            </p>
          </div>
        </div>

        {/* Left Panel — shown second on mobile */}
        <div style={leftPanelStyle}>
          <Link to="/" style={styles.logoSectionLink}>
            <div style={logoSectionStyle}>
              <img src="AstraVision_Logo.png" alt="Astra Vision Logo" style={logoStyle} />
            </div>
          </Link>

          <h1 style={heroTitleStyle}>
            Modern Education<br/>
            <span style={styles.gradientText}>Platform</span>
          </h1>

          <p style={styles.heroSubtitle}>
            Empowering institutions with innovative technology for seamless management, enhanced learning experiences, and data-driven academic excellence.
          </p>

          <div style={featureBadgesStyle}>
            {badges.map((badge, idx) => (
              <div
                key={idx}
                style={badgeStyleMobile}
                onClick={() => navigate('/school-management/all-features')}
              >
                <i className={badge.icon} style={styles.badgeIcon}></i>
                {badge.label}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    fontFamily: "'Inter', sans-serif",
    background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 35%, #93C5FD 70%, #DBEAFE 100%)',
    backgroundAttachment: 'fixed',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'auto',
    willChange: 'auto',
    padding: '16px',
  },
  bgOrbs: {
    position: 'fixed',
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    top: 0,
    left: 0,
    zIndex: 1,
  },
  orb1: {
    position: 'absolute',
    width: '600px',
    height: '600px',
    top: '-200px',
    left: '-100px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(30, 58, 138, 0.15))',
    backdropFilter: 'blur(50px)',
    animation: 'float 6s ease-in-out infinite',
    willChange: 'transform',
  },
  orb2: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    bottom: '-100px',
    right: '-50px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(30, 58, 138, 0.15))',
    backdropFilter: 'blur(50px)',
    animation: 'float 6s ease-in-out infinite 2s',
    willChange: 'transform',
  },
  orb3: {
    position: 'absolute',
    width: '300px',
    height: '300px',
    top: '50%',
    left: '10%',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(30, 58, 138, 0.15))',
    backdropFilter: 'blur(50px)',
    animation: 'float 6s ease-in-out infinite 4s',
    willChange: 'transform',
  },
  container: {
    position: 'relative',
    zIndex: 10,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    maxWidth: '1600px',
    width: '100%',
    background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
    borderRadius: '24px',
    overflow: 'hidden',
    boxShadow: '0 50px 100px rgba(15, 23, 42, 0.6)',
    animation: 'glow 4s ease-in-out infinite',
    backdropFilter: 'blur(20px)',
    margin: '0 auto',
  },
  leftPanel: {
    padding: '60px 50px',
    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.5) 0%, rgba(96, 165, 250, 0.6) 50%, rgba(147, 197, 253, 0.65) 100%)',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  logoSectionLink: {
    textDecoration: 'none',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.3s ease',
  },
  logoSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    marginBottom: '40px',
    padding: '30px 40px',
    background: '#FFFFFF',
    borderRadius: '20px',
    boxShadow: '0 4px 12px rgba(11, 60, 93, 0.15)',
    transition: 'all 0.3s ease',
    width: '100%',
    maxWidth: '400px',
  },
  logo: {
    width: '350px',
    height: 'auto',
    objectFit: 'contain',
    filter: 'drop-shadow(0 2px 4px rgba(11, 60, 93, 0.1))',
  },
  heroTitle: {
    fontSize: '48px',
    fontWeight: '900',
    color: 'white',
    lineHeight: '1.1',
    marginBottom: '20px',
    letterSpacing: '-2px',
    animation: 'slide-up 0.8s ease',
    textAlign: 'center',
  },
  gradientText: {
    background: 'linear-gradient(135deg, #60A5FA 0%, #93C5FD 50%, #DBEAFE 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroSubtitle: {
    fontSize: '16px',
    color: 'rgba(255, 255, 255, 0.9)',
    lineHeight: '1.8',
    marginBottom: '40px',
    animation: 'slide-up 1s ease',
    textAlign: 'center',
  },
  featureBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    animation: 'slide-up 1.2s ease',
    justifyContent: 'center',
    width: '100%',
  },
  badge: {
    padding: '12px 20px',
    background: 'rgba(255, 255, 255, 0.15)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '50px',
    fontSize: '13px',
    fontWeight: '700',
    color: 'white',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
    transition: 'all 0.3s',
    cursor: 'pointer',
    position: 'relative',
    whiteSpace: 'nowrap',
  },
  badgeIcon: {
    marginRight: '8px',
  },
  rightPanel: {
    padding: '60px 50px',
    background: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '500px',
  },
  loginBox: {
    width: '100%',
    maxWidth: '450px',
  },
  loginHeader: {
    marginBottom: '40px',
  },
  loginTitle: {
    fontSize: '40px',
    fontWeight: '900',
    color: '#0F172A',
    marginBottom: '10px',
  },
  loginSubtitle: {
    fontSize: '17px',
    color: 'rgba(15, 23, 42, 0.6)',
  },
  inputGroup: {
    marginBottom: '25px',
  },
  inputLabel: {
    display: 'block',
    fontSize: '16px',
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: '10px',
  },
  inputField: {
    width: '100%',
    padding: '18px 20px',
    border: '2px solid rgba(147, 197, 253, 0.3)',
    background: 'rgba(255, 255, 255, 0.98)',
    color: '#0F172A',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: "'Inter', sans-serif",
    transition: 'all 0.3s',
    boxSizing: 'border-box',
  },
  signinBtn: {
    width: '100%',
    padding: '20px',
    background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #1E40AF 100%)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '12px',
    fontSize: '18px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
    marginTop: '30px',
  },
  footerText: {
    marginTop: '30px',
    textAlign: 'center',
    fontSize: '13px',
    color: 'rgba(15, 23, 42, 0.5)',
  },
  footerLink: {
    color: '#C4B5FD',
    textDecoration: 'none',
    fontWeight: '600',
  },
};
